import React from "react";
import { formatKm, formatNumber } from "../format";
import { StatCard, vehicleLabel } from "./shared";
import { DeliveryNoteCard } from "./deliveryNotes";

export function renderVehicles(questionId, data, { translate, money }) {
  if (questionId === "totalVehicles") {
    const vehicles = data.vehicles || [];
    const fleetSize = data.fleetSize || { total: 0, categories: [] };
    const otherVehicles = Math.max(0, vehicles.length - Number(fleetSize.total || 0));
    const maxCategory = Math.max(
      ...((fleetSize.categories || []).map((item) => item.value) || [0]),
      1
    );

    return (
      <>
        <div className="fleetbot-stats">
          <StatCard
            label={translate("fleetBotTotalVehicles")}
            value={formatNumber(vehicles.length)}
          />
          <StatCard
            label={translate("fleetBotFleetMaintenanceVehicles")}
            value={formatNumber(fleetSize.total)}
            hint={translate("totalFleetSize")}
            accent
          />
          {otherVehicles > 0 ? (
            <StatCard
              label={translate("fleetBotOtherVehicles")}
              value={formatNumber(otherVehicles)}
            />
          ) : null}
        </div>
        {fleetSize.categories?.length ? (
          <div className="fleetbot-list">
            {fleetSize.categories.map((item) => {
              const pct = Math.round((item.value / maxCategory) * 100);
              const labelKey = item.name === "car" ? "CAR" : item.name;
              return (
                <div key={item.name} className="fleetbot-meter-row">
                  <div className="fleetbot-meter-label">
                    <span>{translate(labelKey)}</span>
                    <strong>{formatNumber(item.value)}</strong>
                  </div>
                  <div className="fleetbot-meter">
                    <span style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        ) : null}
      </>
    );
  }

  if (questionId === "servedVehicles") {
    const throughput = data.throughput || {};
    const served = Number(throughput.totalServedVehicles) || 0;
    const notes = Number(throughput.totalDeliveryNotesCount) || 0;
    const fleet = Number(throughput.totalVehicles) || 0;
    const coverage = fleet > 0 ? Math.round((served / fleet) * 100) : 0;

    return (
      <div className="fleetbot-summary">
        <div className="fleetbot-summary-hero">
          <div className="fleetbot-summary-kicker">
            {translate("fleetBotThroughputSummary")}
          </div>
          <div className="fleetbot-summary-hero-value">{formatNumber(served)}</div>
          <div className="fleetbot-summary-hero-label">
            {translate("fleetBotServedVehicles")}
          </div>
          {fleet > 0 ? (
            <>
              <div className="fleetbot-meter fleetbot-summary-meter">
                <span style={{ width: `${Math.min(coverage, 100)}%` }} />
              </div>
              <div className="fleetbot-summary-hint">
                {coverage}% {translate("fleetBotOfFleet")}
              </div>
            </>
          ) : null}
        </div>
        <div className="fleetbot-stats">
          <StatCard
            label={translate("fleetBotTotalDeliveryNotes")}
            value={formatNumber(notes)}
          />
          <StatCard
            label={translate("fleetBotTotalVehicles")}
            value={formatNumber(fleet)}
          />
        </div>
      </div>
    );
  }

  if (questionId === "mileageSummary") {
    const vehicles = data.vehicles || [];
    const total = vehicles.reduce((sum, item) => sum + Number(item.odometer || 0), 0);
    const avg = vehicles.length ? total / vehicles.length : 0;
    const ranked = [...vehicles].sort((a, b) => b.odometer - a.odometer);

    return (
      <>
        <div className="fleetbot-stats">
          <StatCard label={translate("fleetBotTotalMileage")} value={formatKm(total)} />
          <StatCard label={translate("fleetBotAverageMileage")} value={formatKm(avg)} />
        </div>
        <div className="fleetbot-list">
          {ranked.map((item) => {
            const max = ranked[0]?.odometer || 1;
            const pct = Math.round((item.odometer / max) * 100);
            return (
              <div key={item.id} className="fleetbot-meter-row">
                <div className="fleetbot-meter-label">
                  <span>{vehicleLabel(item)}</span>
                  <strong>{formatKm(item.odometer)}</strong>
                </div>
                <div className="fleetbot-meter">
                  <span style={{ width: `${pct}%` }} />
                </div>
              </div>
            );
          })}
        </div>
      </>
    );
  }

  if (questionId === "vehicleDetails") {
    const vehicle = data.vehicle || {};
    const notes = data.deliveryNotes || [];
    const summary = data.deliveryNoteSummary || {};

    return (
      <>
        <div className="fleetbot-vehicle-head">
          <strong>{vehicleLabel(vehicle)}</strong>
          <span className="fleetbot-badge">{vehicle.plateNumber || "—"}</span>
        </div>
        <div className="fleetbot-summary-hero">
          <div className="fleetbot-summary-kicker">
            {translate("fleetBotCurrentOdometer")}
          </div>
          <div className="fleetbot-summary-hero-value">
            {formatKm(vehicle.odometer)}
          </div>
          <div className="fleetbot-summary-hero-label">
            {vehicle.name || vehicle.plateNumber}
          </div>
        </div>
        <div className="fleetbot-stats" style={{ marginTop: 10 }}>
          <StatCard
            label={translate("fleetBotTotalDeliveryNotes")}
            value={formatNumber(summary.totalDeliveryNotes || notes.length)}
            accent
          />
          <StatCard
            label={translate("fleetBotTotalAmount")}
            value={money(summary.totalAmount)}
          />
          <StatCard
            label={translate("fleetBotInvoicedAmount")}
            value={money(summary.invoicedAmount)}
          />
          <StatCard
            label={translate("fleetBotNonInvoicedAmount")}
            value={money(summary.nonInvoicedAmount)}
          />
        </div>
        <div className="fleetbot-section-title">{translate("deliveryNotes")}</div>
        <div className="fleetbot-list">
          {notes.length ? (
            notes.map((item) => (
              <DeliveryNoteCard
                key={item.id || item.deliveryNoteNumber}
                item={item}
                translate={translate}
                money={money}
                showPlate={false}
              />
            ))
          ) : (
            <div className="fleetbot-empty-inline">{translate("fleetBotNoData")}</div>
          )}
        </div>
      </>
    );
  }

  return null;
}
